import { SCHOOLS, GRADE_LEVELS } from "../../constants/collections";
import { gradeLevels } from "../../constants/gradeLevels";
import { ThemeColors } from "../../helpers/ThemeColors";
import { hexToRgb } from "../../helpers/Utils";
import isEmpty from "lodash/isEmpty";

export const DASHBOARD_LOADING_CHART = "DASHBOARD_LOADING_CHART",
  DASHBOARD_SELECT_SCHOOL = "DASHBOARD_SELECT_SCHOOL",
  DASHBOARD_WIDGET_MODE = "DASHBOARD_WIDGET_MODE",
  GET_DASHBOARD_STATS_START = "GET_DASHBOARD_STATS_START",
  GET_DASHBOARD_STATS_SUCCESS = "GET_DASHBOARD_STATS_SUCCESS",
  GET_DASHBOARD_STATS_FAIL = "GET_DASHBOARD_STATS_FAIL";

export const dashboardIsLoadingChart = ({ isLoading, chart }) => {
  return (dispatch) => {
    dispatch({
      type: DASHBOARD_LOADING_CHART,
      isLoading,
      chart,
    });
  };
};

export const dashboardSelectSchool = ({ school, chart }) => {
  return (dispatch) => {
    dispatch({
      type: DASHBOARD_SELECT_SCHOOL,
      selected: school,
      chart,
    });
  };
};

export const dashboardWidgetMode = ({ mode, chart }) => {
  return (dispatch) => {
    dispatch({
      type: DASHBOARD_WIDGET_MODE,
      mode,
      chart,
    });
  };
};

export const getDashboardStats =
  (syOverride, schoolIdOverride) =>
  async (dispatch, getState, { getFirestore }) => {
    dispatch({ type: GET_DASHBOARD_STATS_START });
    try {
      const db = getFirestore();

      const state = getState();
      const currentSy = state.sy.current;

      // Use override if provided, else use current SY from state
      const sy = syOverride || currentSy;

      // Fallback: If no schoolId passed, try to get from user profile
      let activeSchoolId = schoolIdOverride;
      if (!activeSchoolId) {
        const profile = state.firebase.profile;
        if (profile && profile.schools && profile.schools.length > 0) {
          activeSchoolId = profile.schools[0];
        }
      }

      let stats = {
        admissions: 0,
        enrollment: 0,
        revenue: 0,
        applications: 0,
      };
      let enrolledCounts = {};
      let admittedCounts = {};
      let enrollmentByGrade = null;
      let enrollmentGenderSplit = { total: 0, male: 0, female: 0 };
      let usedAggregate = false;

      let offeredGradeLevels = [];

      // 1. Try to fetch Aggregated Grade Counts and Gender Split
      if (activeSchoolId) {
        try {
          // Fetch Grade Levels Aggregate
          const gradeLevelRef = db
            .collection(SCHOOLS)
            .doc(activeSchoolId)
            .collection(GRADE_LEVELS)
            .doc(sy);

          // Fetch Gender/Roster Aggregate
          const studentsRosterRef = db
            .collection(SCHOOLS)
            .doc(activeSchoolId)
            .collection("students")
            .doc(sy);

          const schoolDocRef = db.collection(SCHOOLS).doc(activeSchoolId);

          const [gradeLevelDoc, rosterDoc, schoolDoc] = await Promise.all([
            gradeLevelRef.get(),
            studentsRosterRef.get(),
            schoolDocRef.get(),
          ]);

          if (schoolDoc.exists) {
            const schoolData = schoolDoc.data();
            const schoolLevels = schoolData.schoolLevels || [];

            // Filter global gradeLevels based on schoolLevels (subjects)
            // This matches the logic in AdmitExistingStudentsForm
            offeredGradeLevels = gradeLevels
              .filter((g) => schoolLevels.includes(g.subjects))
              .sort((a, b) => a.sort - b.sort)
              .map((g) => g.value); // Extract codes like "G1", "G2"
          }

          if (gradeLevelDoc.exists) {
            const data = gradeLevelDoc.data();
            stats.enrollment =
              data.enrollments?.TOTAL ||
              data.admissions?.TOTAL ||
              data.TOTAL ||
              0; // Prefer enrollments TOTAL

            // Extract grade counts from new structure
            const admissionsData = data.admissions || {};
            const enrollmentsData = data.enrollments || {};

            // Fallback for legacy (flat structure) if admissions/enrollments missing
            // But user said "restructure", so strictly we should look for them?
            // Let's implement robustly:

            // 1. Populate Admitted Counts
            if (!isEmpty(admissionsData)) {
              Object.keys(admissionsData).forEach((key) => {
                if (key !== "TOTAL") {
                  admittedCounts[key] = admissionsData[key];
                }
              });
            } else {
              // Legacy fallback: treat root keys as admissions
              Object.keys(data).forEach((key) => {
                if (
                  key !== "TOTAL" &&
                  key !== "admissions" &&
                  key !== "enrollments"
                ) {
                  admittedCounts[key] = data[key];
                }
              });
            }

            // 2. Populate Enrolled Counts
            if (!isEmpty(enrollmentsData)) {
              Object.keys(enrollmentsData).forEach((key) => {
                if (key !== "TOTAL") {
                  enrolledCounts[key] = enrollmentsData[key];
                }
              });
            }

            usedAggregate = true;
          }

          // Ensure all offered grades are in counts (default to 0)
          offeredGradeLevels.forEach((grade) => {
            if (enrolledCounts[grade] === undefined) enrolledCounts[grade] = 0;
            if (admittedCounts[grade] === undefined) admittedCounts[grade] = 0;
          });

          if (rosterDoc.exists) {
            const data = rosterDoc.data();
            // Source TOTAL ENROLLED strictly from 'enrolled' array if available
            const total = data.enrolled
              ? data.enrolled.length
              : data.students
                ? data.students.length
                : 0;
            const male = data.male ? data.male.length : 0;
            const female = data.female ? data.female.length : 0;

            // Override stats.enrollment with correct enrolled count
            stats.enrollment = total;

            enrollmentGenderSplit = {
              total,
              male,
              female,
            };

            // Use aggregate for applications count if available
            if (data.applicants && Array.isArray(data.applicants)) {
              stats.applications = data.applicants.length;
            }
          }

          // Safeguard: If we found no grade counts (no aggregate keys AND no offered grades config),
          // force fallback to loop to see if we can find anything manually.
          // Safeguard: If we found no grade counts (no aggregate keys AND no offered grades config),
          // force fallback to loop to see if we can find anything manually.
          if (
            Object.keys(admittedCounts).length === 0 &&
            Object.keys(enrolledCounts).length === 0
          ) {
            console.log(
              "getDashboardStats: No grade counts found in aggregate/config. Forcing fallback.",
            );
            usedAggregate = false;
          }
        } catch (e) {
          console.warn(
            "Failed to fetch aggregate stats, falling back to iteration",
            e,
          );
        }
      }

      // 2. Fetch Lists (Optimized queries if possible, but for now we might need to iterate if we didn't get aggregate?
      // Actually, if we have aggregate, we can just fetch limits for lists.
      // BUT we still need 'applications' count. There is no aggregate for that yet.
      // So we might still need to query students.

      // For now, let's keep the iteration for Applications count and lists,
      // but override Enrollment stats if we have aggregate.
      // Ideally we should make an aggregate for applications too, but that's out of scope.

      const studentsRef = db.collection("students");
      let query = studentsRef;
      if (activeSchoolId) {
        query = query.where("schoolId", "==", activeSchoolId);
      }
      // If we used aggregate for enrollment, we only strictly need applications count and the small lists.
      // However, Firestore doesn't support "count()" efficiently in older SDKs without reading.
      // So we might still read all to count applications? Or we rely on the loop.

      const snapshot = await query.get();

      let recentApplications = [];
      let newEnrollees = [];

      // Reset if we are falling back
      // Reset if we are falling back or re-counting specific stats
      // Reset if we are falling back or re-counting specific stats
      // Reset if we are falling back
      // Reset if we are falling back or re-counting specific stats
      if (!usedAggregate) {
        // Reset counters if we are re-counting from scratch
        offeredGradeLevels.forEach((grade) => {
          enrolledCounts[grade] = 0;
          admittedCounts[grade] = 0;
        });
      }

      stats.enrollment = 0; // Always recount strictly enrolled students from the snapshot loop
      enrollmentGenderSplit = { total: 0, male: 0, female: 0 };

      snapshot.forEach((doc) => {
        const data = doc.data();
        const status = data.admissionStatus || data.status;
        const grade = data.gradeLevel || "Unknown";

        // Initialize if not present (e.g. Unknown grade or new ones not in offered)
        if (enrolledCounts[grade] === undefined) enrolledCounts[grade] = 0;
        if (admittedCounts[grade] === undefined) admittedCounts[grade] = 0;

        // Stats Counting logic
        if (["ADMITTED", "ASSESSED", "ENROLLED"].includes(status)) {
          // All these count as "Admitted" (Approved to enroll)
          admittedCounts[grade]++;

          if (status === "ENROLLED") {
            stats.enrollment++;
            enrolledCounts[grade]++;

            // Gender Split Calculation
            enrollmentGenderSplit.total++;
            const g = (data.gender || "").toLowerCase();
            if (g === "male") enrollmentGenderSplit.male++;
            if (g === "female") enrollmentGenderSplit.female++;
          }

          if (newEnrollees.length < 10 && status === "ENROLLED") {
            newEnrollees.push({ id: doc.id, ...data });
          }
        }

        if (
          status === "NEW" ||
          status === "APPLICANT" ||
          status === "PENDING"
        ) {
          if (!usedAggregate) {
            stats.applications++;
          }
          stats.admissions++;
          if (recentApplications.length < 5) {
            recentApplications.push({ id: doc.id, ...data });
          }
        }
      });

      // Helper to get label and sort
      const getGradeMeta = (code) => {
        return gradeLevels.find((gl) => gl.value === code);
      };

      const getGradeLabel = (code) => {
        const found = getGradeMeta(code);
        return found ? found.label : code;
      };

      const getGradeSort = (code) => {
        const found = getGradeMeta(code);
        return found ? found.sort : 999;
      };

      // Prepare Chart Data
      // Use keys from one of the count objects (they are synced)
      const sortedKeys = Object.keys(enrolledCounts).sort((a, b) => {
        return getGradeSort(a) - getGradeSort(b);
      });

      const colors = ThemeColors();
      const primaryColor = colors.themeColor1; // Enrolled (Blue-ish usually)
      const secondaryColor = colors.themeColor2; // Admitted (Orange-ish usually)

      const primaryRgb = hexToRgb(primaryColor);
      const secondaryRgb = hexToRgb(secondaryColor);

      const primaryBg = primaryRgb
        ? `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`
        : "rgba(20, 83, 136, 0.1)";
      const secondaryBg = secondaryRgb
        ? `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.1)`
        : "rgba(226, 134, 59, 0.1)";

      enrollmentByGrade = {
        labels: sortedKeys,
        fullLabels: sortedKeys.map(getGradeLabel),
        datasets: [
          {
            label: "Enrolled",
            data: sortedKeys.map((k) => enrolledCounts[k]),
            borderColor: primaryColor,
            backgroundColor: primaryBg,
            borderWidth: 2,
            pointBackgroundColor: primaryColor,
          },
          {
            label: "Admitted",
            data: sortedKeys.map((k) => admittedCounts[k]),
            borderColor: secondaryColor,
            backgroundColor: secondaryBg,
            borderWidth: 2,
            pointBackgroundColor: secondaryColor,
          },
        ],
      };

      dispatch({
        type: GET_DASHBOARD_STATS_SUCCESS,
        payload: {
          stats,
          recentApplications,
          newEnrollees,
          enrollmentByGrade,
          enrollmentGenderSplit,
        },
      });
    } catch (error) {
      console.error("Dashboard Stats Error:", error);
      dispatch({ type: GET_DASHBOARD_STATS_FAIL, error });
    }
  };
